import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Star, ArrowRight, Phone, Camera, MessageSquare, CalendarSync, ShieldAlert, Download } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { FAQSection } from "@/components/FAQSection";
import { CTABand } from "@/components/CTABand";
import { SITE, IMAGES, RENTAL_PDF } from "@/content/site";

const FREQUENCIES = [
  {
    visits: "1x",
    label: "Weekly Visit",
    best: "Lower occupancy & off-season",
    text: "Full professional service every week — water stays balanced, clean, and ready for the next booking even when the calendar is light.",
  },
  {
    visits: "2x",
    label: "Weekly Visits",
    best: "Steady booking calendars",
    text: "A midweek reset plus a pre-weekend check, so arriving guests always find clear, inviting water no matter when check-in lands.",
  },
  {
    visits: "3x",
    label: "Weekly Visits",
    best: "High-occupancy & peak season",
    text: "Near-constant water quality control for beach houses and high-turnover properties where the pool is the headline amenity.",
  },
];

const WHY_POINTS = [
  {
    icon: Star,
    title: "The pool is your most reviewed amenity",
    text: "Guests mention the pool in reviews more than almost anything else — good or bad. Professional service keeps it in the five-star column.",
  },
  {
    icon: CalendarSync,
    title: "Turnovers don't wait for chemistry",
    text: "Back-to-back bookings mean the pool must be guest-ready on a schedule, not whenever the water decides to clear. Frequent visits keep it ahead of the calendar.",
  },
  {
    icon: ShieldAlert,
    title: "Heavy use changes water fast",
    text: "Sunscreen, sand, and a full house of swimmers burn through sanitizer in hours, not days. Rental chemistry needs managing between guests, not just once a week.",
  },
  {
    icon: MessageSquare,
    title: "Problems need reporting, not discovering",
    text: "When something's wrong, you hear it from us first — with photos — not from a guest's one-star review after checkout.",
  },
];

const VISIT_INCLUDES = [
  "Professional water testing & chemical balancing every visit",
  "Skimming, brushing & vacuuming as needed",
  "Basket emptying and waterline detail before arrivals",
  "Filter checks and scheduled filter cleaning",
  "Salt system inspection for saltwater rentals",
  "Equipment check with prompt photo alerts for any issue",
];

const RENTAL_FAQS = [
  {
    q: "Do you offer pool service for Airbnbs and vacation rentals?",
    a: "Yes — we're a trusted pool care partner of multiple property management companies in Pinellas County. Short-term rental pools can be serviced 1x, 2x, or 3x weekly depending on occupancy, with photo documentation and prompt issue alerts so a pool problem never becomes your next bad review. We treat your rental business like our own.",
  },
  {
    q: "How often should a vacation rental pool be serviced?",
    a: "It depends on occupancy and season. Lightly booked properties often do well with 1x weekly service; steady calendars benefit from 2x weekly visits; high-occupancy beach properties in peak season are best protected with 3x weekly visits. We'll recommend a schedule based on your booking pattern.",
  },
  {
    q: "Can you coordinate with our property manager or turnover schedule?",
    a: "Yes — that's how we already work with the property management companies we partner with. We align visits around turnovers and send photo documentation and issue alerts directly to you or your manager.",
  },
  {
    q: "What happens if a guest reports a pool problem mid-stay?",
    a: "Contact us and we'll assess the issue and communicate clearly about what it takes to fix it. Frequent service visits catch most problems before guests ever see them.",
  },
];

export default function VacationRentalPage() {
  return (
    <>
      <SEO
        title="Vacation Rental & Airbnb Pool Service in Pinellas County FL | Point of Pines"
        description="Short-term rental pool service for Airbnbs, VRBOs and vacation homes across Pinellas County FL. 1x, 2x & 3x weekly visits, photo reports, and trusted property management partnerships. Call (727) 744-5908."
        path="/vacation-rental-pool-service/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Vacation Rental Pool Service",
          provider: {
            "@type": "HomeAndConstructionBusiness",
            name: SITE.name,
            telephone: "+1-727-744-5908",
            url: SITE.domain,
          },
          areaServed: { "@type": "AdministrativeArea", name: "Pinellas County, FL" },
        }}
      />
      <PageHero
        overline="Short-Term Rental Pool Service — Pinellas County, FL"
        title="Vacation Rental Pool Care"
        accent="That Protects Your Reviews"
        description="Specialized pool service for Airbnbs, VRBOs, and vacation homes — with 1x, 2x, and 3x weekly visit schedules built around your booking calendar, not ours."
        image={IMAGES.rentalBeachPool}
        imageAlt="Beachside vacation rental pool and spa at dusk in Pinellas County Florida maintained with weekly service visits"
      />

      <section data-testid="rental-why-section" className="py-16 sm:py-24 lg:py-32 bg-shell">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-pool font-semibold">Why It Matters</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
              A rental pool is a revenue asset, not a backyard pool
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Guests arrive expecting hotel-clear water — and they photograph everything. A cloudy
              pool on arrival day doesn&apos;t just cost you a refund request; it costs you the
              review that sells your next ten bookings. Here&apos;s why professional service is
              non-negotiable for short-term rentals.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
            {WHY_POINTS.map((p, i) => (
              <motion.div
                key={p.title}
                data-testid={`rental-why-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
                className="bg-white rounded-2xl border border-slate-200/80 p-7 sm:p-8 transition-shadow duration-300 hover:shadow-xl hover:shadow-cyan-500/10"
              >
                <span className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center">
                  <p.icon className="w-6 h-6 text-sky2" />
                </span>
                <h3 className="mt-5 font-display text-xl font-semibold text-navy tracking-tight leading-snug">{p.title}</h3>
                <p className="mt-3 text-sm sm:text-[15px] text-slate-500 leading-relaxed">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="rental-schedules-section" className="py-16 sm:py-24 bg-navy text-white relative overflow-hidden">
        <div className="noise-overlay absolute inset-0 opacity-[0.04] pointer-events-none" />
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto relative">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-sky2 font-semibold">Visit Schedules</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15]">
              Choose the coverage your calendar demands
            </h2>
            <p className="mt-5 text-base text-slate-300 leading-relaxed">
              Every rental runs differently. We match visit frequency to your occupancy, season,
              and how hard your pool works — and we can adjust as your booking calendar changes.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-5 lg:gap-6">
            {FREQUENCIES.map((f, i) => (
              <motion.div
                key={f.visits}
                data-testid={`rental-frequency-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-white/[0.06] backdrop-blur-sm ring-1 ring-white/15 rounded-2xl p-7 sm:p-8 transition-colors duration-300 hover:bg-white/10"
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-br from-sky2 to-aqua">
                    {f.visits}
                  </span>
                  <span className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400">{f.label}</span>
                </div>
                <p className="mt-4 text-[11px] font-mono uppercase tracking-[0.16em] text-aqua">{f.best}</p>
                <p className="mt-3 text-sm sm:text-[15px] text-slate-300 leading-relaxed">{f.text}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-14 grid lg:grid-cols-2 gap-10 items-start">
            <Reveal>
              <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">Every rental visit includes</h3>
              <ul className="mt-6 space-y-3.5">
                {VISIT_INCLUDES.map((item, i) => (
                  <li key={item} data-testid={`rental-included-${i}`} className="flex items-start gap-3.5">
                    <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-aqua" />
                    </span>
                    <span className="text-sm sm:text-[15px] text-slate-300 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="bg-white/[0.06] backdrop-blur-sm ring-1 ring-white/15 rounded-2xl p-7 sm:p-8">
                <div className="flex gap-1 text-aqua">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="mt-4 font-display font-semibold tracking-tight leading-snug text-lg">
                  &ldquo;Your guests rate the pool before they unpack. We make sure it earns five
                  stars every single check-in.&rdquo;
                </p>
                <p className="mt-4 text-sm text-slate-300 leading-relaxed">
                  We&apos;re a trusted pool care partner of multiple property management companies
                  across Pinellas County — and we treat your rental business like our own.
                </p>
                <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-[11px] font-mono uppercase tracking-[0.16em] text-sky2/80">
                  <span className="inline-flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Photo Reports</span>
                  <span className="inline-flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Direct Line to a Local Pro</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <FAQSection
        faqs={RENTAL_FAQS}
        overline="Rental FAQ"
        title="Questions rental owners & managers ask"
      />

      <section data-testid="rental-partner-cta" className="py-16 sm:py-20 bg-mist">
        <div className="px-4 sm:px-6 lg:px-12 max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
              One property or a whole portfolio — we scale with you
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Tell us about your rental (or your portfolio) and your booking pattern. We&apos;ll
              recommend a visit schedule and walk you through exactly how we keep your pool
              review-proof.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3.5">
              <Link
                to="/contact/"
                data-testid="rental-page-quote-button"
                className="group inline-flex items-center justify-center gap-2.5 bg-navy text-white font-bold px-8 py-4 rounded-full transition-all duration-300 hover:bg-pool hover:text-white hover:shadow-lg hover:shadow-cyan-500/25"
              >
                Get a Rental Service Quote
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={SITE.phoneHref}
                data-testid="rental-page-call-button"
                className="inline-flex items-center justify-center gap-2.5 border-2 border-navy text-navy font-bold px-8 py-4 rounded-full transition-all duration-300 hover:bg-navy hover:text-white"
              >
                <Phone className="w-5 h-5" /> {SITE.phone}
              </a>
            </div>
            <a
              href={RENTAL_PDF}
              download
              data-testid="rental-download-pdf-button"
              className="group mt-5 inline-flex items-center gap-2.5 font-display font-bold text-pool hover:text-ocean transition-colors"
            >
              <Download className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              Download our rental service sheet (PDF) — perfect for owner presentations
            </a>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Stop worrying about your rental's pool"
        text="Join the property managers and owners across Pinellas County who trust Point of Pines with their reviews."
      />
    </>
  );
}
